// src/pages/dashboards/user/UserSubscription.jsx
// Plan cards + a manual-EFT upgrade flow, plus prepaid scan top-ups. Banking
// details come from the API; "I've paid" flags the payment for recon. Discount
// codes apply in the upgrade modal (percentage/fixed reduce the amount due).
//
// Paid tiers share an identical feature set and differ only on scan allowance,
// so features are listed once below the cards rather than as a comparison grid.

import React, { useEffect, useState } from "react";
import {
  getSubscription, getPlans, upgradeIntent, getBankingDetails,
  voucherPreview, getWebOffer, getPacks, packIntent,
} from "../../../api/user";
import { Card, Button, Badge, Loading, Input, Modal } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import { money, date, titleCase } from "../../../lib/format";

const FEATURE_LABEL = {
  exportPack: "SARS export pack",
  webManage: "Web portal access",
  splits: "Shared deductions",
  practitioner: "Practitioner access",
};
const FEATURE_KEYS = Object.keys(FEATURE_LABEL);

// free is shown as a status line, not a card - it is not purchasable
const PAID_ORDER = ["starter", "pro", "max"];
const TIER_RANK = { free: 0, starter: 1, pro: 2, max: 3 };
// plan callouts. Pro is the middle anchor; max carries the lowest per-scan rate.
const PLAN_BADGE = {
  pro: { tone: "amber", label: "Popular" },
  max: { tone: "blue", label: "Best value" },
};

export default function UserSubscription() {
  const toast = useToast();
  const [sub, setSub] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [upgrade, setUpgrade] = useState(null);
  const [pack, setPack] = useState(null);

  useEffect(() => {
    getSubscription().then(setSub).catch((e) => toast.error(e.message));
    getPlans().then(setPlans).catch(() => {});
    getPacks().then(setPacks).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  if (!sub) return <Loading label="Loading your plan" />;

  const currentKey = sub.tier?.key || sub.subscription?.tier || "free";
  const currentRank = TIER_RANK[currentKey] ?? 0;
  const byKey = Object.fromEntries(plans.map((p) => [p.key, p]));
  const cols = PAID_ORDER.map((k) => byKey[k]).filter(Boolean);
  const freePlan = byKey.free;
  const scanText = (scan) => (scan?.limit == null ? "Unlimited" : `${scan.limit}`);

  return (
    <div>
      {/* current plan line */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">Plan</h1>
          <Badge tone="green">On {sub.tier?.label || titleCase(currentKey)}</Badge>
        </div>
        {sub.subscription?.expires_at && (
          <span className="text-xs text-tb-muted">Renews / ends {date(sub.subscription.expires_at)}</span>
        )}
      </div>

      {/* paid plans */}
      {cols.length === 0 ? <Loading label="Loading plans" /> : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {cols.map((p) => {
            const cur = p.key === currentKey;
            const isUp = (TIER_RANK[p.key] ?? 0) > currentRank;
            const perScan = p.scan?.limit ? p.price / p.scan.limit : null;
            return (
              <Card
                key={p.key}
                className={`p-5 md:p-6 flex flex-col ${cur ? "ring-2 ring-tb-brand" : ""}`}
              >
                {/* mobile keeps name and price on one line to cut the scroll;
                    desktop stacks them so the cards read as columns */}
                <div className="flex items-center justify-between gap-3 md:block">
                  <div className="flex items-center gap-2 md:justify-between md:mb-3 md:min-h-[24px]">
                    <span className="font-bold text-lg">{p.label}</span>
                    {cur
                      ? <Badge tone="green">Current</Badge>
                      : PLAN_BADGE[p.key]
                        ? <Badge tone={PLAN_BADGE[p.key].tone}>{PLAN_BADGE[p.key].label}</Badge>
                        : null}
                  </div>
                  <div className="flex items-baseline gap-1 shrink-0">
                    <span className="text-2xl md:text-3xl font-bold">{money(p.price)}</span>
                    <span className="text-xs md:text-sm text-tb-muted">/yr</span>
                  </div>
                </div>

                <div className="mt-3 md:mt-4 md:pt-4 md:border-t border-tb-border dark:border-tb-d-border flex items-baseline gap-2 md:block">
                  <p className="text-xl md:text-2xl font-bold text-tb-brand leading-none">{scanText(p.scan)}</p>
                  <p className="text-xs text-tb-muted md:mt-1">
                    scans per year{perScan ? ` · ${money(perScan)} each` : ""}
                  </p>
                </div>

                <div className="mt-4 md:mt-6">
                  {cur ? (
                    <Button variant="subtle" className="w-full" disabled>Current plan</Button>
                  ) : isUp ? (
                    <Button className="w-full" onClick={() => setUpgrade(p)}>Upgrade</Button>
                  ) : (
                    <Button variant="ghost" className="w-full" disabled>Included</Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* the paid tiers share a feature set, so it is stated once */}
      {cols.length > 0 && (
        <Card className="mt-4 p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-tb-muted mb-3">
            Included in every paid plan
          </p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-2 gap-x-4">
            {FEATURE_KEYS.map((k) => (
              <div key={k} className="flex items-center gap-2 text-sm">
                <span className="text-tb-brand font-semibold">{"\u2713"}</span>
                <span>{FEATURE_LABEL[k]}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* free is a starting state, not an option to pick */}
      {currentKey === "free" && freePlan && (
        <p className="mt-3 text-xs text-tb-muted">
          You're on Free - {freePlan.scan?.limit ?? 0} scans to try things out. Upgrade for a yearly allowance and the features above.
        </p>
      )}

      {/* prepaid top-ups. Credits stack on the plan allowance and never expire,
          so this is the escape hatch when someone runs out mid-year. */}
      {packs.length > 0 && (
        <div className="mt-8">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-tb-muted mb-3">
            Need more scans?
          </h2>
          {packs.map((p) => (
            <Card key={p.key} className="p-5 mb-3">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-baseline justify-between gap-3 sm:block">
                    <p className="font-bold text-lg">{p.label}</p>
                    <span className="text-xl font-bold text-tb-brand sm:hidden">{money(p.price)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-tb-muted">
                    {money(p.perScan)} per scan · credits never expire and stack on your plan allowance
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="hidden sm:block text-2xl font-bold text-tb-brand">{money(p.price)}</span>
                  <Button variant="subtle" className="w-full sm:w-auto" onClick={() => setPack(p)}>Buy scans</Button>
                </div>
              </div>
            </Card>
          ))}
          <p className="text-xs text-tb-muted">
            Paying by EFT is available to South African customers only.
          </p>
        </div>
      )}

      {upgrade && <UpgradeModal plan={upgrade} onClose={() => setUpgrade(null)} />}
      {pack && <PackModal pack={pack} onClose={() => setPack(null)} />}
    </div>
  );
}

// copyable banking value
function Row({ label, value }) {
  const toast = useToast();
  const copy = () => navigator.clipboard?.writeText(String(value)).then(() => toast.info?.("Copied"), () => {});
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
      <span className="text-tb-muted shrink-0">{label}</span>
      <button onClick={copy} className="font-medium hover:text-tb-brand transition-colors truncate text-right" title="Copy">{value}</button>
    </div>
  );
}

// shared EFT panel for both modals
function BankPanel({ bank, amount, reference }) {
  if (!bank) return <Loading label="Loading details" />;
  return (
    <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
      <Row label="Account name" value={bank.accountName} />
      <Row label="Bank" value={bank.bank} />
      <Row label="Account number" value={bank.accountNumber} />
      <Row label="Branch code" value={bank.branchCode} />
      <Row label="Account type" value={bank.accountType} />
      <Row label="Amount" value={money(amount)} />
      <Row label="Reference" value={reference} />
    </div>
  );
}

function UpgradeModal({ plan, onClose }) {
  const toast = useToast();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState(null);
  const [code, setCode] = useState("");
  const [discount, setDiscount] = useState(null);
  const [checking, setChecking] = useState(false);
  const reference = (user?.email || "your email").toLowerCase();

  useEffect(() => { getBankingDetails().then(setBank).catch(() => setBank({})); }, []);

  // Pre-fill the standing web discount. Paying by EFT costs us nothing in app
  // store commission, and this passes that on without anyone having to know a
  // code exists. They can clear it if they want to.
  useEffect(() => {
    let cancelled = false;
    getWebOffer()
      .then((r) => {
        if (cancelled || !r?.offer) return;
        setCode(r.offer.code);
        setDiscount(r.offer);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const net = (() => {
    if (!discount) return plan.price;
    if (discount.discount_type === "percentage") return Math.max(0, Math.round(plan.price * (1 - discount.discount_value / 100)));
    if (discount.discount_type === "fixed") return Math.max(0, plan.price - discount.discount_value);
    return plan.price;
  })();

  const applyCode = async () => {
    if (!code.trim()) return;
    setChecking(true);
    try {
      const d = await voucherPreview(code.trim());
      if (d.discount_type === "free_months") {
        toast.info?.("That code can't be applied to a payment.");
        setDiscount(null);
      } else {
        setDiscount({ ...d, code: code.trim() });
        toast.success("Discount applied");
      }
    } catch (e) { toast.error(e.message); setDiscount(null); } finally { setChecking(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await upgradeIntent(plan.key, discount?.code);
      toast.success("Thanks - we'll watch for your payment and activate it once it clears");
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const discountLabel = discount
    ? (discount.discount_type === "percentage" ? `${discount.discount_value}% off` : `${money(discount.discount_value)} off`)
    : null;

  return (
    <Modal open onClose={onClose} title={`Upgrade to ${plan.label}`} width="max-w-2xl" bodyClassName="p-6">
      <div className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* left: amount + discount */}
          <div className="space-y-4">
            <div className="rounded-2xl p-5 bg-tb-wash dark:bg-tb-brand/10 border border-tb-brand/20 text-center">
              <p className="text-xs uppercase tracking-wider text-tb-brand/80">Amount due</p>
              {discount ? (
                <div className="mt-1 flex items-baseline justify-center gap-2">
                  <span className="text-lg text-tb-muted line-through">{money(plan.price)}</span>
                  <span className="text-4xl font-bold text-tb-brand leading-none">{money(net)}</span>
                </div>
              ) : (
                <p className="text-4xl font-bold text-tb-brand leading-none mt-1">{money(plan.price)}</p>
              )}
              <p className="mt-2 text-xs text-tb-muted">
                per year · once-off EFT{discountLabel ? ` · ${discountLabel} (${discount.code})` : ""}
              </p>
            </div>

            <div>
              <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Discount code</p>
              <div className="flex gap-2">
                <Input placeholder="Optional" value={code}
                  onChange={(e) => { setCode(e.target.value.toUpperCase()); setDiscount(null); }} />
                <Button variant="subtle" onClick={applyCode} disabled={checking || !code.trim()}>Apply</Button>
              </div>
              {discount && (
                <p className="text-xs text-tb-brand mt-1.5">
                  {discountLabel} applied for paying directly
                </p>
              )}
            </div>
          </div>

          {/* right: EFT details */}
          <div>
            <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Pay by EFT to</p>
            <BankPanel bank={bank} amount={net} reference={reference} />
          </div>
        </div>

        <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-3 text-xs text-tb-muted space-y-1.5">
          <p>Pay the exact amount and use your email as the reference so we can match it. Tap any value to copy.</p>
          <p>Once you've made the EFT, tap "I've paid" - we'll activate {plan.label} once it reflects, usually within 1-2 business days.</p>
          {bank?.payEmail && <p>Need help? {bank.payEmail}</p>}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={markPaid} disabled={busy}>I've paid</Button>
        </div>
      </div>
    </Modal>
  );
}

// Same manual-EFT flow as the upgrade, minus vouchers - packs are not discounted.
function PackModal({ pack, onClose }) {
  const toast = useToast();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [bank, setBank] = useState(null);
  const reference = (user?.email || "your email").toLowerCase();

  useEffect(() => { getBankingDetails().then(setBank).catch(() => setBank({})); }, []);

  const markPaid = async () => {
    setBusy(true);
    try {
      await packIntent(pack.key);
      toast.success("Thanks - we'll add your scans once the payment clears");
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} title={`Buy ${pack.label}`} width="max-w-lg" bodyClassName="p-6">
      <div className="space-y-5">
        <div className="rounded-2xl p-5 bg-tb-wash dark:bg-tb-brand/10 border border-tb-brand/20 text-center">
          <p className="text-xs uppercase tracking-wider text-tb-brand/80">Amount due</p>
          <p className="text-4xl font-bold text-tb-brand leading-none mt-1">{money(pack.price)}</p>
          <p className="mt-2 text-xs text-tb-muted">
            {pack.scans} scans · {money(pack.perScan)} each · once-off EFT
          </p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-wider text-tb-muted mb-2">Pay by EFT to</p>
          <BankPanel bank={bank} amount={pack.price} reference={reference} />
        </div>

        <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-3 text-xs text-tb-muted space-y-1.5">
          <p>Pay the exact amount and use your email as the reference. Tap any value to copy.</p>
          <p>Once you've paid, tap "I've paid" - we'll load your scans once it reflects, usually within 1-2 business days.</p>
          {bank?.payEmail && <p>Need help? {bank.payEmail}</p>}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button onClick={markPaid} disabled={busy}>I've paid</Button>
        </div>
      </div>
    </Modal>
  );
}